import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

import { ScrollDirection, computeScrollDirection } from 'src/components/NavBar/NavBarUtil';
import { isBrowser } from 'src/utils/UserAgent';
import { usePrevious } from './UsePrevious';

/**
 * A hook that captures the current scroll direction of the page
 * @param {boolean} disabled Condition not to observe scroll event
 * @see https://github.corp.clover.com/clover/cloverdotcom/blob/58becc551940bb5388d11810be938505fb1ed42e/src/components/NavBar/NavBar.tsx#L82-L109
 */
export function useScrollDirection({ disabled }: {
  disabled?: boolean;
} = {}): ScrollDirection {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(ScrollDirection.NONE);
  const prevScrollDirection = usePrevious(scrollDirection, { isImmediate: true });
  const prevScrollPositionRef = useRef(isBrowser ? window.pageYOffset : 0);

  useEffect(() => {
    if (!isBrowser || disabled) return;

    const onWindowScroll = debounce(() => {
      const { pageYOffset, innerHeight: viewportHeight } = window;

      const { scrollHeight: bodyHeight } = document.body;

      const maxScroll = Math.max(bodyHeight - viewportHeight, 0);

      const scrollDirection = computeScrollDirection(
        prevScrollPositionRef.current,
        pageYOffset,
        maxScroll
      );

      if (
        scrollDirection === ScrollDirection.NONE ||
        scrollDirection === prevScrollDirection
      ) return;

      prevScrollPositionRef.current = pageYOffset;
      setScrollDirection(scrollDirection);
    });

    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', onWindowScroll);

  }, [disabled]);

  return scrollDirection;
}
